import React from "react";

// Short per-language abbreviations for the chart's tiny house boxes — the
// full names (chart.planets[key].name) are already localized but far too
// long to fit here, so these are separate best-effort short forms.
const PLANET_ABBR = {
  sun: { en: "Su", te: "సూ", hi: "सू", ta: "சூ", kn: "ಸೂ", ml: "സൂ" },
  moon: { en: "Mo", te: "చం", hi: "चं", ta: "சந்", kn: "ಚಂ", ml: "ചന്" },
  mars: { en: "Ma", te: "కు", hi: "मं", ta: "செ", kn: "ಕು", ml: "ചൊ" },
  mercury: { en: "Me", te: "బు", hi: "बु", ta: "பு", kn: "ಬು", ml: "ബു" },
  jupiter: { en: "Ju", te: "గు", hi: "गु", ta: "கு", kn: "ಗು", ml: "വ്യാ" },
  venus: { en: "Ve", te: "శు", hi: "शु", ta: "சு", kn: "ಶು", ml: "ശു" },
  saturn: { en: "Sa", te: "శని", hi: "शनि", ta: "சனி", kn: "ಶನಿ", ml: "ശനി" },
  rahu: { en: "Ra", te: "రా", hi: "रा", ta: "ரா", kn: "ರಾ", ml: "രാ" },
  ketu: { en: "Ke", te: "కే", hi: "के", ta: "கே", kn: "ಕೇ", ml: "കே" },
};

const ASC_LABEL = { en: "As", te: "లగ్న", hi: "लग्न", ta: "லக்", kn: "ಲಗ್ನ", ml: "ലഗ്ന" };

// North Indian chart: houses are fixed shapes on a diamond-in-square
// layout, but which Rasi occupies each house rotates with the Ascendant
// (House 1 is always wherever the Ascendant's Rasi is). Geometry below is
// the classic construction: square + both corner-to-corner diagonals +
// the diamond connecting each side's midpoint — this yields exactly 12
// regions (4 "kite" angle houses touching the center, 8 corner triangles).
// Coordinates in a 300x300 box.
const A = [0, 0], B = [300, 0], C = [300, 300], D = [0, 300];
const MT = [150, 0], MR = [300, 150], MB = [150, 300], ML = [0, 150];
const O = [150, 150];
const P_TL = [75, 75], P_TR = [225, 75], P_BR = [225, 225], P_BL = [75, 225];

const pts = (...p) => p.map((xy) => xy.join(",")).join(" ");

// house 1..12, each: { poly, label position }
const HOUSES = [
  { poly: pts(MT, P_TR, O, P_TL), label: [150, 55] },       // 1
  { poly: pts(A, MT, P_TL), label: [75, 28] },               // 2
  { poly: pts(A, P_TL, ML), label: [28, 75] },                // 3
  { poly: pts(ML, P_TL, O, P_BL), label: [58, 150] },        // 4
  { poly: pts(ML, D, P_BL), label: [28, 225] },               // 5
  { poly: pts(D, MB, P_BL), label: [75, 272] },               // 6
  { poly: pts(MB, P_BR, O, P_BL), label: [150, 245] },       // 7
  { poly: pts(MB, C, P_BR), label: [225, 272] },              // 8
  { poly: pts(C, MR, P_BR), label: [272, 225] },              // 9
  { poly: pts(MR, P_TR, O, P_BR), label: [242, 150] },       // 10
  { poly: pts(P_TR, B, MR), label: [272, 75] },               // 11
  { poly: pts(MT, B, P_TR), label: [225, 28] },               // 12
];

export default function KundaliniChart({ chart, label, t }) {
  if (!chart) return null;
  const ascRashi = chart.ascendant.rashi_index;

  // House N holds whatever Rasi is (ascRashi + N - 1) mod 12 — chart.houses
  // is already indexed 0..11 by rashi_index, so this is a direct lookup.
  const houseData = HOUSES.map((h, i) => {
    const rashiIdx = (ascRashi + i) % 12;
    return { ...h, houseNum: i + 1, planets: chart.houses[rashiIdx].planets };
  });

  return (
    <div className="kundalini-chart">
      <svg viewBox="0 0 300 300" className="kundalini-svg" role="img" aria-label={t("kundaliniTitle")}>
        <rect x="1" y="1" width="298" height="298" className="kundalini-outline" />
        <polyline points={pts(A, C)} className="kundalini-line" />
        <polyline points={pts(B, D)} className="kundalini-line" />
        <polyline points={pts(MT, MR, MB, ML, MT)} className="kundalini-line" />

        {houseData.map((h) => (
          <polygon key={h.houseNum} points={h.poly} className="kundalini-house-fill" />
        ))}

        {houseData.map((h) => {
          const isAsc = h.houseNum === 1;
          return (
            <text
              key={h.houseNum}
              x={h.label[0]}
              y={h.label[1]}
              textAnchor="middle"
              className={`kundalini-house-text${isAsc ? " kundalini-asc-text" : ""}`}
            >
              <tspan x={h.label[0]} dy="0" className="kundalini-house-num">
                {isAsc ? label(ASC_LABEL) : h.houseNum}
              </tspan>
              {h.planets.map((pKey) => {
                const p = chart.planets[pKey];
                return (
                  <tspan key={pKey} x={h.label[0]} dy="12" className="kundalini-house-planet">
                    {label(PLANET_ABBR[pKey])}
                    {p.retrograde ? " R" : ""}
                    {p.combust ? " C" : ""}
                  </tspan>
                );
              })}
            </text>
          );
        })}
      </svg>
      <div className="kundalini-ascendant-line">
        {t("kundaliniAscendant")}: {label(chart.ascendant.rashi)}
      </div>
    </div>
  );
}
